import { AgentsIcon, AlarmClockIcon, ArrowRightIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { useI18n } from "~/i18n";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import { activityCardActions, type ActivityCardActionId } from "./activityCardActions";
import type { ActivityEvent } from "./activitiesTypes";
import {
  activityKindIcon,
  activityKindLabelKey,
  activityStatusBadgeClass,
  activityStatusLabelKey,
  formatActivityTime,
  toolkitLogoUrl,
} from "./activityPresentation";

/** Fixed height so stack layers and expanded list align visually. */
export const ACTIVITY_CARD_MIN_HEIGHT_CLASS = "min-h-[10.5rem]";

export function ActivityCard({
  event,
  selected,
  onSelect,
  onAction,
  isActionBusy,
  className,
  uniformHeight = true,
}: {
  readonly event: ActivityEvent;
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly onAction?: ((actionId: ActivityCardActionId, event: ActivityEvent) => void) | undefined;
  readonly isActionBusy?: (actionId: ActivityCardActionId) => boolean;
  readonly className?: string;
  readonly uniformHeight?: boolean;
}) {
  const { t, locale } = useI18n();
  const [logoBroken, setLogoBroken] = useState(false);
  const logo = toolkitLogoUrl(event.refs.toolkit);
  const actions = onAction ? activityCardActions(event) : [];
  const KindIcon = activityKindIcon(event.kind);
  // Show a due time only where "due" is a real thing the user waits on.
  const showDue =
    !!event.dueAt && (event.status === "needs_approval" || event.status === "pending");

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex flex-col cursor-pointer border-border/70 bg-card p-3 transition-colors hover:bg-muted/40",
        uniformHeight && ACTIVITY_CARD_MIN_HEIGHT_CLASS,
        selected && "ring-2 ring-primary/35 bg-muted/30",
        className,
      )}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <MorrowPersonAvatar
              name={event.actor.name}
              avatarSeed={event.actor.avatarSeed}
              size="sm"
              className="shrink-0"
            />
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
              {event.actor.name}
            </span>
            <div className="flex max-w-[48%] shrink-0 items-center justify-end gap-1.5">
              <Badge
                variant="outline"
                className={cn(
                  "h-5 max-w-[6.5rem] shrink truncate px-1.5 text-[10px] font-medium leading-none",
                  activityStatusBadgeClass(event.status),
                )}
              >
                {t(activityStatusLabelKey(event.status))}
              </Badge>
              <span className="shrink-0 text-[10px] leading-none tabular-nums text-muted-foreground">
                {formatActivityTime(event.occurredAt, locale)}
              </span>
            </div>
          </div>
          <p className="truncate text-base font-semibold leading-snug text-foreground">
            {event.title}
          </p>
          <p className="line-clamp-2 min-h-0 flex-1 text-xs leading-relaxed text-muted-foreground">
            {event.summary}
          </p>
        </div>
        <div className="mt-auto flex shrink-0 items-end justify-between gap-2 pt-1.5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <KindIcon className="size-3 shrink-0" aria-hidden />
              {t(activityKindLabelKey(event.kind))}
            </span>
            {event.kind === "comms" && event.refs.toAgentLabel ? (
              // Agent-to-agent flow: sender is the actor (top); show the recipient here.
              <span className="inline-flex max-w-full items-center gap-1 truncate rounded bg-muted px-1.5 py-0.5">
                <ArrowRightIcon className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{event.refs.toAgentLabel}</span>
              </span>
            ) : event.refs.agentLabel ? (
              <span className="inline-flex max-w-full items-center gap-1 truncate rounded bg-muted px-1.5 py-0.5">
                <AgentsIcon className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{event.refs.agentLabel}</span>
              </span>
            ) : null}
            {showDue ? (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlarmClockIcon className="size-3 shrink-0" aria-hidden />
                due {formatActivityTime(event.dueAt!, locale)}
              </span>
            ) : null}
            {logo && !logoBroken ? (
              <img
                src={logo}
                alt=""
                className="size-3.5 rounded object-contain"
                loading="lazy"
                onError={() => setLogoBroken(true)}
              />
            ) : null}
          </div>
          {actions.length > 0 ? (
            <div
              className="flex shrink-0 flex-wrap items-center justify-end gap-1.5"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {actions.map((action) => (
                <Button
                  key={action.id}
                  type="button"
                  size="compact"
                  variant={action.variant}
                  className="h-7 px-2 text-[11px]"
                  disabled={isActionBusy?.(action.id)}
                  onClick={() => onAction?.(action.id, event)}
                >
                  {t(action.labelKey)}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
